import React, { useState, useRef, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { parseJobUrl } from '../../services/api'
import './ImportJobModal.scss'

interface ImportJobModalProps {
  isOpen: boolean
  onClose: () => void
  onImport: (jobData: any) => Promise<void>
  isImporting: boolean
  userCredits?: number
}

interface FormData {
  title: string
  company: string
  companyWebsite: string
  location: string
  description: string
  url: string
}

interface FormErrors {
  title?: string
  company?: string
  companyWebsite?: string
  url?: string
  description?: string
}

function ImportJobModal({ isOpen, onClose, onImport, isImporting, userCredits }: ImportJobModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [formData, setFormData] = useState<FormData>({
    title: '',
    company: '',
    companyWebsite: '',
    location: '',
    description: '',
    url: ''
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isParsingUrl, setIsParsingUrl] = useState(false)
  const [confirmChecked, setConfirmChecked] = useState(false)
  const urlInputRef = useRef<HTMLInputElement>(null)

  // Focus URL field when modal opens
  useEffect(() => {
    if (isOpen && urlInputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        urlInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleParseUrl = async () => {
    const url = formData.url.trim()
    if (!url || isParsingUrl || isImporting) {
      return
    }

    // Basic URL validation
    try {
      new URL(url)
    } catch {
      // Show error for invalid URL
      setErrors(prev => ({
        ...prev,
        url: 'Please enter a valid URL'
      }))
      return
    }

    setIsParsingUrl(true)
    // Clear any previous URL errors
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors.url
      return newErrors
    })

    try {
      const parsedData = await parseJobUrl(url, getAccessTokenSilently)
      
      // Always use parsed data when available (user clicked Auto-fill to populate fields)
      setFormData(prev => ({
        ...prev,
        title: parsedData.title || prev.title || '',
        company: parsedData.company || prev.company || '',
        companyWebsite: parsedData.companyWebsite || prev.companyWebsite || '',
        location: parsedData.location || prev.location || '',
        description: parsedData.description || prev.description || '',
        url: url // Keep the entered URL
      }))
    } catch (error) {
      // Don't log expected client errors (invalid/unreachable URLs)
      // These are user-facing errors, not internal errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isClientError = 
        errorMessage.includes('400') ||
        errorMessage.includes('Bad Request') ||
        errorMessage.includes('Unable to fetch') ||
        errorMessage.includes('Invalid URL') ||
        errorMessage.includes('Failed to fetch');
      
      if (!isClientError) {
        // Only log unexpected errors
        logger.error('Error parsing job URL', {
          error: errorMessage,
          url,
        });
      }
      
      setErrors(prev => ({
        ...prev,
        url: 'Unable to parse this URL. Please check the URL or enter the job details manually.'
      }))
    } finally {
      setIsParsingUrl(false)
    }
  }

  // Validate domain format (e.g., example.com, www.example.com, subdomain.example.co.uk)
  const isValidDomain = (domain: string): boolean => {
    if (!domain.trim()) return false
    // Remove protocol if present
    let cleanDomain = domain.trim().toLowerCase()
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '')
    cleanDomain = cleanDomain.replace(/^www\./, '')
    cleanDomain = cleanDomain.split('/')[0] // Remove path if present
    
    // Domain regex: allows subdomains, TLDs with multiple parts (e.g., .co.uk)
    const domainRegex = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(\.[a-z]{2,})*$/i
    return domainRegex.test(cleanDomain)
  }

  const validate = (): FormErrors => {
    const newErrors: FormErrors = {}
    if (!formData.url.trim()) {
      newErrors.url = 'Job posting URL is required'
    } else {
      // Validate URL format
      try {
        new URL(formData.url.trim())
      } catch {
        newErrors.url = 'Please enter a valid URL'
      }
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required'
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required'
    }
    if (!formData.companyWebsite.trim()) {
      newErrors.companyWebsite = 'Company website is required'
    } else if (!isValidDomain(formData.companyWebsite)) {
      newErrors.companyWebsite = 'Please enter a valid domain (e.g., example.com)'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required'
    }
    return newErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      await onImport(formData)
      // Reset form on success
      setFormData({
        title: '',
        company: '',
        companyWebsite: '',
        location: '',
        description: '',
        url: ''
      })
      setErrors({})
      setConfirmChecked(false)
      onClose()
    } catch (error) {
      logger.error('Error importing job', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  const handleClose = () => {
    if (!isImporting) {
      setFormData({
        title: '',
        company: '',
        companyWebsite: '',
        location: '',
        description: '',
        url: ''
      })
      setErrors({})
      setConfirmChecked(false)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Import Job Posting</h3>
          <button 
            type="button" 
            className="modal-close" 
            onClick={handleClose}
            disabled={isImporting}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {userCredits !== undefined && userCredits <= 0 && (
              <div className="alert alert-warning mb-3" role="alert">
                <strong>No credits remaining!</strong> You need at least 1 credit to import a job posting. 
                Please purchase more credits to continue.
              </div>
            )}
            <div className="form-group">
              <label htmlFor="url" className="form-label">
                Job Posting URL <span className="text-danger">*</span>
              </label>
              <div className="d-flex gap-2">
                <input
                  ref={urlInputRef}
                  type="url"
                  id="url"
                  name="url"
                  className={`form-control ${errors.url ? 'is-invalid' : ''}`}
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://..."
                  disabled={isImporting || isParsingUrl}
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleParseUrl}
                  disabled={!formData.url.trim() || isImporting || isParsingUrl}
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {isParsingUrl ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Parsing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-magic me-1"></i>
                      Auto-fill
                    </>
                  )}
                </button>
              </div>
              {errors.url && (
                <div className="invalid-feedback d-block">{errors.url}</div>
              )}
              {formData.url && !isParsingUrl && !errors.url && (
                <small className="form-text text-muted">
                  Paste a job posting URL and click "Auto-fill" to populate the form
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="title" className="form-label">
                Job Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Senior Software Engineer"
                disabled={isImporting}
                required
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="company" className="form-label">
                Company <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                className={`form-control ${errors.company ? 'is-invalid' : ''}`}
                value={formData.company}
                onChange={handleChange}
                placeholder="e.g., Tech Corp"
                disabled={isImporting}
                required
              />
              {errors.company && (
                <div className="invalid-feedback">{errors.company}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="companyWebsite" className="form-label">
                Company Website <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="companyWebsite"
                name="companyWebsite"
                className={`form-control ${errors.companyWebsite ? 'is-invalid' : ''}`}
                value={formData.companyWebsite}
                onChange={handleChange}
                placeholder="e.g., example.com or www.example.com"
                disabled={isImporting}
                required
              />
              {errors.companyWebsite && (
                <div className="invalid-feedback">{errors.companyWebsite}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="location" className="form-label">
                Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                className="form-control"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., San Francisco, CA or Remote"
                disabled={isImporting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Description <span className="text-danger">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                rows={5}
                value={formData.description}
                onChange={handleChange}
                placeholder="Paste the job description here..."
                disabled={isImporting}
                required
              />
              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>

            <div className="form-group">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="confirmImport"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  disabled={isImporting}
                  required
                />
                <label className="form-check-label" htmlFor="confirmImport">
                  I confirm the above information is correct, and 1 credit will be spent when the job is imported. <span className="text-danger">*</span>
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
              disabled={isImporting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isImporting || !confirmChecked || (userCredits !== undefined && userCredits <= 0)}
            >
              {isImporting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Importing...
                </>
              ) : (
                'Spend 1 credit and import job'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ImportJobModal
